import useMediaQuery from '@mui/material/useMediaQuery';
import {
  AmountInput,
  MultiSelect,
  MultiSelectOption,
  theme,
  Typography,
} from '@raisin/components-lib';
import { useT } from '@transifex/react';
import messages from 'messages';
import React, { FC, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { FinancialInstitutionType, ProductOfferListItem, USProductOffer } from 'types';
import { DesktopItem } from './DesktopItem';
import { HeaderColumnsRow } from './HeaderColumnsRow';
import { MobileItem } from './MobileItem';
import { sortOffers } from './sorting';
import * as S from './styles';
import {
  calculateEarnings,
  financialInstitutionOptions,
  formatMaturity,
  formatPercents,
  OfferSortingKey,
  openProductTerms,
  SortDirection,
} from './utils';
import { getUSPostLoginBaseUrl } from '@raisin/cp-utils';

interface TableContentProps {
  offers: USProductOffer[];
  isSavingsOffer?: boolean;
  bankLogoLinks?: Record<string, string>;
  financialInstitutions?: Record<string, FinancialInstitutionType[]>;
  selectedFilters: MultiSelectOption[];
  onFilterChange: (sel: MultiSelectOption[]) => void;
  showFinancialInstitutionsDropdown: boolean;
}

export const ProductOfferTableContent: FC<TableContentProps> = ({
  offers,
  isSavingsOffer = false,
  financialInstitutions,
  selectedFilters,
  bankLogoLinks = {},
  onFilterChange,
  showFinancialInstitutionsDropdown,
}) => {
  const t = useT();
  const { formatNumber } = useIntl();
  const [depositAmount, setDepositAmount] = useState<number>(50000);
  const [loadingProductId, setLoadingProductId] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [sortColumn, setSortColumn] = useState<OfferSortingKey>('apy');
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [chunks, setChunks] = useState(1);
  const chunkSize = 5;

  const handleColumnClick = (column: OfferSortingKey) => {
    // If a new column is clicked, reset sort order to 'desc' and update active sort column
    if (column !== sortColumn) {
      const textColumns = ['bankName', 'productName'];
      setSortDirection(textColumns.includes(column) ? 'asc' : 'desc');
      setSortColumn(column);
      return;
    }

    // If the same column is clicked, toggle the sort order
    setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  };

  const handleAmountChange = (e) => {
    const val = e.target.value;
    const numeric = Number(val.replace(/\D/g, ''));

    setDepositAmount(numeric);
  };

  const amountInputId = 'amount-input';

  const selectedFilterValues = useMemo(
    () => selectedFilters.map((filter) => filter.value as FinancialInstitutionType),
    [selectedFilters],
  );

  let preparedOffers: ProductOfferListItem[] = useMemo(
    () =>
      offers
        .map(({ id, type, apy, name, maturity, productBank }) => ({
          id,
          type,
          productName: name,
          apy: apy / 100,
          maturityDuration: maturity?.duration || 0,
          maturityUnits: maturity?.unit || 'MONTHS',
          bankName: productBank.name,
          bankLogo: productBank.logoUrl,
          bankId: productBank.id,
          financialInstitutions: financialInstitutions?.[productBank.id] || [],
        }))
        .filter(
          ({ financialInstitutions }) =>
            selectedFilterValues.length === 0 ||
            financialInstitutions?.some((institution) =>
              selectedFilterValues.includes(institution),
            ),
        ),
    [offers, financialInstitutions, selectedFilterValues],
  );
  const sortedOffers = sortOffers([...preparedOffers], sortColumn, sortDirection, isSavingsOffer);

  const offersToDisplay = [...sortedOffers].splice(0, chunks * chunkSize);
  const displayLoadMoreBtn = offersToDisplay.length !== sortedOffers.length;

  return (
    <>
      <S.InputContainer>
        <S.InputWrapper>
          <AmountInput
            formatNumber={formatNumber}
            fullWidth
            data-testid={amountInputId}
            id={amountInputId}
            name={amountInputId}
            value={depositAmount}
            label={'Deposit Amount'}
            onChange={handleAmountChange}
            autoComplete="off"
            allowNegative={false}
            decimalScale={0}
            externalLabel={false}
            InputProps={{
              startAdornment: <Typography ml={4}>$</Typography>,
            }}
            sx={{ paddingLeft: 0 }}
            InputLabelProps={{ shrink: true }}
          />
        </S.InputWrapper>
        {showFinancialInstitutionsDropdown && (
          <S.MultiSelectWrapper>
            <MultiSelect
              label="Financial Institutions"
              options={financialInstitutionOptions}
              onChange={onFilterChange}
              value={selectedFilters}
              limitTags={3}
              testId="InstitutionsFilter"
            />
          </S.MultiSelectWrapper>
        )}
      </S.InputContainer>
      <HeaderColumnsRow
        onColumnClick={handleColumnClick}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        isSavingsOffer={isSavingsOffer}
      />
      {offersToDisplay.map((offer) => {
        const props = {
          offer,
          loadingProductId,
          bankLogoLink: bankLogoLinks[offer.bankId],
          openProductTerms: (offerId) => {
            setLoadingProductId(offerId);
            openProductTerms(offerId).finally(() => setLoadingProductId(''));
          },
          apyDisplay: formatPercents(offer.apy, { maximumFractionDigits: 2 }),
          earnings: calculateEarnings(offer.apy, depositAmount),
          maturityDisplay: formatMaturity(offer.maturityDuration, offer.maturityUnits),
          isSavingsOffer,
          isCallableCD: offer.productName.includes('Callable'),
          onInvest: (productId) => {
            const url = `${getUSPostLoginBaseUrl()}/auth/register?productId=${productId}`;

            window.location.href = url;
          },
        };
        const Component = isMobile ? MobileItem : DesktopItem;

        return <Component key={offer.id} {...props} />;
      })}
      {displayLoadMoreBtn && (
        <S.Footer>
          <S.LoadMoreButton
            mode="secondary"
            data-testid="load-more-btn"
            onClick={() => {
              setChunks((prev) => prev + 1);
            }}
          >
            {t(messages.usOfferTableLoadMore)}
          </S.LoadMoreButton>
        </S.Footer>
      )}
    </>
  );
};
